import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, Phone, MapPin, Calendar, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { LINKS, COMPANY } from "@/config/links";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  fleet_size: "",
  service_interest: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await axios.post(`${API}/contact`, payload);
      setDone(true);
      setForm(initial);
      toast.success("Message received. We’ll be in touch within one business day.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Toaster theme="dark" position="top-right" richColors />

      {/* HEADER */}
      <section className="relative pt-20 sm:pt-28 pb-12 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[420px] gradient-radial-blue pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Get in touch</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white leading-[1.05]">
              Tell us about <span className="text-[#FFCB05]">your operation</span>.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
              Send a message — we’ll respond within one business day. Or book a time on our calendar directly below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="relative pb-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-8">
          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="relative p-8 sm:p-10 rounded-2xl border border-white/5 bg-gradient-to-b from-[#00274C]/40 to-[#040914]">
              {done && (
                <div data-testid="contact-success" className="mb-6 p-4 rounded-xl border border-[#FFCB05]/30 bg-[#FFCB05]/5 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#FFCB05] mt-0.5" />
                  <div>
                    <p className="font-display text-white text-sm font-semibold">Message received.</p>
                    <p className="text-sm text-[#9CA3AF] mt-0.5">We’ll be in touch within one business day. Want to skip the wait? Book a callback on the right.</p>
                  </div>
                </div>
              )}

              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Full name" name="name" value={form.name} onChange={onChange} required testid="contact-input-name" />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="contact-input-email" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Company (optional)" name="company" value={form.company} onChange={onChange} testid="contact-input-company" />
                  <Field label="Phone (optional)" name="phone" value={form.phone} onChange={onChange} testid="contact-input-phone" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SelectField
                    label="Fleet size"
                    name="fleet_size"
                    value={form.fleet_size}
                    onChange={onChange}
                    testid="contact-input-fleet-size"
                    options={["1 truck (owner-operator)", "2–5 trucks", "6+ trucks (custom)"]}
                  />
                  <SelectField
                    label="Service of interest"
                    name="service_interest"
                    value={form.service_interest}
                    onChange={onChange}
                    testid="contact-input-service"
                    options={["Monthly Bookkeeping", "IFTA quarterly prep", "1099 & W-9 tracking", "Setup / catch-up", "Custom Engagement", "Not sure yet"]}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#9CA3AF] mb-2">Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    required
                    rows={5}
                    data-testid="contact-input-message"
                    placeholder="Tell us a little about your operation, current pain points, and what you'd like to fix."
                    className="w-full rounded-xl bg-[#040914] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#FFCB05]/50 focus:ring-2 focus:ring-[#FFCB05]/20 transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] disabled:opacity-60 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5"
                >
                  {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>) : (<>Send Message <Send className="h-4 w-4" /></>)}
                </button>
              </form>
            </div>
          </div>

          {/* SIDE INFO + CALENDLY */}
          <div className="lg:col-span-5 space-y-5">
            <div className="p-7 rounded-2xl border border-white/5 bg-[#0A111E]">
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Reach us</p>
              <ul className="mt-4 space-y-3 text-sm text-[#9CA3AF]">
                <li className="flex items-start gap-3"><Mail className="h-4 w-4 text-[#FFCB05] mt-0.5" /><a data-testid="contact-email-link" href={`mailto:${COMPANY.email}`} className="hover:text-white">{COMPANY.email}</a></li>
                <li className="flex items-start gap-3"><Phone className="h-4 w-4 text-[#FFCB05] mt-0.5" /><a data-testid="contact-phone-link" href={`tel:${COMPANY.phoneTel}`} className="hover:text-white">{COMPANY.phone}</a></li>
                <li className="flex items-start gap-3"><MapPin className="h-4 w-4 text-[#FFCB05] mt-0.5" /><span>{COMPANY.location}</span></li>
              </ul>
              <p className="mt-5 text-xs text-[#6B7280]">{COMPANY.hours}</p>
            </div>

            <div className="p-7 rounded-2xl border border-white/5 bg-gradient-to-b from-[#00274C]/45 to-[#040914]">
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Book the 30-minute call</p>
              <h3 className="mt-2 font-display text-xl font-semibold text-white">See if Linehaul is a fit.</h3>
              <p className="mt-2 text-sm text-[#9CA3AF]">Thirty minutes. Fit, trucks, ELD, term. Not a pitch deck.</p>
              <a
                href={LINKS.discoveryCall}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-calendly-btn"
                className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border border-[#FFCB05] text-[#FFCB05] hover:bg-[#FFCB05]/10 transition-all"
              >
                See if Linehaul is a fit <Calendar className="h-4 w-4" />
              </a>
            </div>

            <div className="p-7 rounded-2xl border border-white/5 bg-[#0A111E]">
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Already a client?</p>
              <p className="mt-2 text-sm text-[#9CA3AF]">Sign in to your Linehaul Client Portal with your Client ID.</p>
              <Link
                to="/portal"
                data-testid="contact-portal-btn"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/40 hover:bg-white/5 transition-all"
              >
                Open Client Portal
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#9CA3AF] mb-2">{label}{required && <span className="text-[#FFCB05]"> *</span>}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full rounded-xl bg-[#040914] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#FFCB05]/50 focus:ring-2 focus:ring-[#FFCB05]/20 transition-colors"
      />
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#9CA3AF] mb-2">{label}</label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full rounded-xl bg-[#040914] border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FFCB05]/50 focus:ring-2 focus:ring-[#FFCB05]/20 transition-colors"
      >
        <option value="">Select…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
